import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, Star, Heart } from "lucide-react";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandCard from "@/components/brand/BrandCard";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
import { fetchProperty } from "@/lib/api";
import { useFavourites } from "@/contexts/FavouritesProvider";
import { cn } from "@/lib/utils";

const VelaProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [room, setRoom] = useState(null);
  const { has, toggle } = useFavourites();

  useEffect(() => { fetchProperty(id).then((d) => { setP(d); setRoom(d.rooms?.[0] || null); }); }, [id]);
  if (!p) return <div className="container-rail py-16">Loading…</div>;

  // Gallery shows ONLY the property's curated gallery — the hero is rendered
  // separately above and must never repeat as the first carousel item.
  // Defensively de-duplicate and strip any accidental match against the hero
  // so the seed data and the carousel never disagree.
  const galleryImages = Array.from(
    new Set((p.gallery || []).filter((g) => g && g !== p.hero_image))
  );

  return (
    <div className="pb-32 md:pb-0" data-testid="vela-property">
      <div className="relative aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden">
        <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" />
      </div>
      <div className="container-rail py-10 md:py-14 grid lg:grid-cols-[1.6fr_1fr] gap-10">
        <div>
          <BrandLabel withRule>Vela · {p.country}</BrandLabel>
          <BrandHeading level={1} className="mt-4">{p.name}</BrandHeading>
          <div className="mt-3 flex items-center gap-4 text-[13px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MapPin size={13} /> {p.location}</span>
            <span className="inline-flex items-center gap-1"><Star size={13} className="fill-current" /> {p.rating?.toFixed(1)} · {p.review_count} guests</span>
          </div>
          {p.tagline && (
            <p
              className="mt-4 font-display italic text-[20px]"
              style={{ fontFamily: "var(--font-accent)" }}
            >
              “{p.tagline}”
            </p>
          )}
          <p className="mt-6 text-[16px] md:text-[17px] font-body editorial-measure leading-[1.75]">
            {p.long_description || p.description}
          </p>

          <div className="mt-10">
            <div className="flex items-center justify-between">
              <BrandLabel>Gallery</BrandLabel>
              <span className="text-[12px] text-muted-foreground">{galleryImages.length} images</span>
            </div>
            <Carousel className="mt-4">
              <CarouselContent>
                {galleryImages.map((src, i) => (
                  <CarouselItem key={`${src}-${i}`} className="basis-2/3 md:basis-1/2 lg:basis-1/3">
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious /><CarouselNext />
            </Carousel>
          </div>

          <Separator className="my-10" />
          <div>
            <BrandLabel>The Rooms</BrandLabel>
            <BrandHeading level={2} className="mt-3">Rooms &amp; Apartments</BrandHeading>
            <div className="mt-6 grid gap-5">
              {p.rooms?.map((r) => (
                <BrandCard
                  key={r.id}
                  className={cn(
                    "grid md:grid-cols-[200px_1fr_auto] gap-5 p-4 md:p-5 cursor-pointer transition-colors",
                    room?.id === r.id && "border-[hsl(var(--primary))]"
                  )}
                  onClick={() => setRoom(r)}
                  data-testid={`vela-room-${r.id}`}
                >
                  <img src={r.image} alt={r.name} className="w-full h-32 md:h-full object-cover rounded-[8px]" />
                  <div>
                    <div className="font-display text-[22px]">{r.name}</div>
                    <p className="mt-2 text-[13px] text-muted-foreground">{r.description}</p>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      {r.amenities?.map((a) => <span key={a}>{a}</span>)} · <span>{r.size_sqm} m²</span> · <span>Sleeps {r.max_guests}</span>
                    </div>
                  </div>
                  <div className="text-right md:flex md:flex-col md:items-end md:justify-between">
                    <div className="font-display text-[24px]">€{r.price_per_night}<span className="text-[12px] text-muted-foreground"> / n</span></div>
                    <BrandButton
                      size="sm"
                      className="mt-3"
                      onClick={(e) => { e.stopPropagation(); navigate(`/vela/book/${p.id}?room=${r.id}`); }}
                      data-testid={`vela-select-${r.id}`}
                    >
                      Book Stay
                    </BrandButton>
                  </div>
                </BrandCard>
              ))}
            </div>
          </div>

          <Separator className="my-10" />
          <div>
            <BrandLabel>The House</BrandLabel>
            <BrandHeading level={2} className="mt-3">Amenities</BrandHeading>
            <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-[14px] font-body">
              {p.amenities?.map((a) => (
                <div key={a} className="py-2 border-b border-border">{a}</div>
              ))}
            </div>
          </div>
        </div>
        <aside className="lg:sticky lg:top-24 self-start">
          <BrandCard className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <BrandLabel>Book</BrandLabel>
                <div className="mt-2 font-display text-[26px]">
                  €{room?.price_per_night || p.price_from}
                  <span className="text-[13px] text-muted-foreground font-body"> / night</span>
                </div>
              </div>
              <button
                onClick={() => toggle(p.id, p.name)}
                aria-label="Save"
                data-testid="vela-fav"
                className="w-10 h-10 inline-flex items-center justify-center border border-border hover:border-[hsl(var(--primary))]"
              >
                <Heart size={16} className={cn(has(p.id) && "fill-current text-[hsl(var(--primary))]")} />
              </button>
            </div>
            <p className="mt-3 text-[13px] text-muted-foreground">{room?.name || "Choose a room below."}</p>
            <BrandButton
              className="mt-5 w-full"
              onClick={() => navigate(`/vela/book/${p.id}${room ? `?room=${room.id}` : ""}`)}
              data-testid="vela-reserve"
            >
              Book Your Stay
            </BrandButton>
            <BrandButton variant="secondary" className="mt-3 w-full" to="/vela/search" data-testid="vela-back">
              Back to Stays
            </BrandButton>
          </BrandCard>
        </aside>
      </div>
      <div
        className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-background border-t border-border p-3 flex items-center justify-between gap-3"
        data-testid="vela-sticky"
      >
        <div>
          <div className="text-[11px] text-muted-foreground">{room?.name || "From"}</div>
          <div className="font-display text-[18px]">€{room?.price_per_night || p.price_from} / night</div>
        </div>
        <BrandButton
          onClick={() => navigate(`/vela/book/${p.id}${room ? `?room=${room.id}` : ""}`)}
          data-testid="vela-sticky-reserve"
        >
          Book
        </BrandButton>
      </div>
    </div>
  );
};

export default VelaProperty;
